import React, { useEffect, useState } from "react";
import { Building2 } from "lucide-react";
import DashboardCard from "../components/DashboardCard";
import DashboardCardSkeleton from "../components/DashboardCardSkeleton";
import DashboardEmptyState from "../components/DashboardEmptyState";
import DashboardPagination from "../components/DashboardPagination";
import { DASHBOARD_PORTFOLIO_PAGE_SIZE } from "../constants/dashboardListPagination";
import { useOwnerPortfolio } from "../hooks/useOwnerPortfolio";
import { PortfolioProperty } from "../types/ownerPortfolio.types";
import { paginateItemsLocally } from "../utils/paginateItemsLocally";
import PortfolioKpis from "./PortfolioKpis";
import PortfolioPropertyCard from "./PortfolioPropertyCard";
import PortfolioPropertyTable from "./PortfolioPropertyTable";
import PortfolioToolbar from "./PortfolioToolbar";

interface OwnerPortfolioProps {
  accessToken: string | null;
  enabled: boolean;
  smDown: boolean;
}

function getCardPadding(smDown: boolean) {
  return smDown ? "0.875rem" : "1.125rem";
}

function hasActiveFilters(situation: string | null, condominium: string | null) {
  return Boolean(situation || condominium);
}

export default function OwnerPortfolio({
  accessToken,
  enabled,
  smDown,
}: OwnerPortfolioProps) {
  const portfolio = useOwnerPortfolio({ accessToken, enabled });
  const [page, setPage] = useState(1);
  const properties = portfolio.data?.properties ?? [];
  const { items: visibleProperties, pagination } = paginateItemsLocally(
    properties,
    page,
    DASHBOARD_PORTFOLIO_PAGE_SIZE
  );
  const filterKey = `${portfolio.query.situation ?? ""}|${portfolio.query.condominium ?? ""}|${portfolio.query.sort}|${portfolio.query.metricsDays}`;

  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  useEffect(() => {
    if (page > pagination.last_page) {
      setPage(pagination.last_page);
    }
  }, [page, pagination.last_page]);

  if (!enabled || portfolio.status === "idle") {
    return null;
  }

  if (portfolio.status === "loading" && !portfolio.data) {
    return <DashboardCardSkeleton minHeight="min-h-[18rem]" lines={6} />;
  }

  if (portfolio.status === "error" && !portfolio.data) {
    return (
      <DashboardCard padding={getCardPadding(smDown)}>
        <DashboardEmptyState
          align="center"
          icon={Building2}
          title="Carteira indisponível"
          description="Não foi possível carregar os imóveis. Tente novamente em instantes."
          action={
            <button
              type="button"
              onClick={() => void portfolio.retry()}
              className="rounded-full bg-[#6ea8ff] px-4 py-2 text-xs font-bold text-[#0d1117]"
            >
              Tentar novamente
            </button>
          }
        />
      </DashboardCard>
    );
  }

  if (!portfolio.data) {
    return null;
  }

  const filtersActive = hasActiveFilters(
    portfolio.query.situation,
    portfolio.query.condominium
  );

  return (
    <DashboardCard padding={getCardPadding(smDown)} id="meus-imoveis">
      <div className="mb-4 flex items-start gap-3">
        <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-[#6ea8ff]" aria-hidden />
        <div className="min-w-0">
          <h2 className="m-0 text-[clamp(1rem,1.4vw,1.25rem)] font-semibold text-[#f0f6fc]">
            Meus Imóveis
          </h2>
          <p className="mt-1 text-xs text-[#8b949e] sm:text-sm">
            Situação, valor e desempenho de cada imóvel da carteira
          </p>
        </div>
      </div>

      <PortfolioKpis kpis={portfolio.data.kpis} />

      <div className="mt-4">
        <PortfolioToolbar
          query={portfolio.query}
          condominiums={portfolio.data.condominiums}
          situationLabels={portfolio.data.situation_labels}
          onSituationChange={portfolio.setSituation}
          onCondominiumChange={portfolio.setCondominium}
          onSortChange={portfolio.setSort}
          onMetricsDaysChange={portfolio.setMetricsDays}
        />
      </div>

      {portfolio.status === "error" && (
        <p className="mt-3 text-xs text-[#f85149]">
          Não foi possível atualizar a carteira.{" "}
          <button type="button" className="underline" onClick={() => void portfolio.retry()}>
            Tentar novamente
          </button>
        </p>
      )}

      <div className="mt-4" aria-busy={portfolio.status === "loading"}>
        {visibleProperties.length === 0 ? (
          <DashboardEmptyState
            align="center"
            title="Nenhum imóvel encontrado"
            description={
              filtersActive || portfolio.data.kpis.total > 0
                ? "Nenhum imóvel corresponde aos filtros selecionados."
                : "Sua carteira ainda não tem imóveis administrados."
            }
            action={
              filtersActive ? (
                <button
                  type="button"
                  onClick={portfolio.clearFilters}
                  className="rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-[#f0f6fc]"
                >
                  Limpar filtros
                </button>
              ) : undefined
            }
          />
        ) : smDown ? (
          <div className="flex flex-col gap-3" data-testid="portfolio-cards">
            {visibleProperties.map((property: PortfolioProperty, index) => (
              <PortfolioPropertyCard
                key={`${property.kenlo_property_id}-${property.contract_code ?? index}`}
                property={property}
                accessToken={accessToken}
              />
            ))}
          </div>
        ) : (
          <PortfolioPropertyTable properties={visibleProperties} accessToken={accessToken} />
        )}
      </div>

      <DashboardPagination
        currentPage={pagination.current_page}
        totalPages={pagination.last_page}
        onPageChange={setPage}
      />
    </DashboardCard>
  );
}
